import { Component, computed, effect, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AgGridModule } from 'ag-grid-angular';
import { IconCellRendererComponent } from '../../util/icon-cell-renderer-component/icon-cell-renderer-component';
import { RowClassRules, ValueGetterParams } from 'ag-grid-enterprise';
import { TechnicalEligibilityApiService } from '../../../services/technical-eligibility-api-service';
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatButtonModule } from '@angular/material/button';
import { IconButtonCellRendererComponent } from '../../util/icon-button-cell-renderer-component/icon-button-cell-renderer-component';
import { TechnicalEligibilityTechnicalInfoModalComponent } from '../technical-eligibility-technical-info-modal-component/technical-eligibility-technical-info-modal-component';
import { TechnicalEligibilityFibreBuildingsModalComponent } from '../technical-eligibility-fibre-buildings-modal-component/technical-eligibility-fibre-buildings-modal-component';
import { ProviderResponseNameCellRendererComponent } from '../../util/provider-response-name-cell-renderer-component/provider-response-name-cell-renderer-component';

@Component({
  selector: 'app-technical-eligibility-site-detail-modal-component',
  imports: [AgGridModule, MatDialogModule, MatIconModule, MatTooltipModule, MatButtonModule, ClipboardModule],
  templateUrl: './technical-eligibility-site-detail-modal-component.html',
})
export class TechnicalEligibilitySiteDetailModalComponent {
  public readonly gridLocale = AG_GRID_LOCALE_FR; 
  public readonly dataSignal = signal<any>(undefined);

  public readonly rowClassRules: RowClassRules = {
    'row-warning': (params) => { return params.data.fibreBuildingCodesMismatch; },
  }

  public readonly rowData = computed<any[]>(() => {
    const data = this.dataSignal();
    return Object.keys(data).map((providerKey: string) => {
      if (!data[providerKey]) return;

      return {
        provider: providerKey,
        state: data[providerKey].feasibility,
        maxDownloadRate: data[providerKey].maxDownloadRate,
        maxUploadRate: data[providerKey].maxUploadRate,
        pricingZones: data[providerKey].pricingZones,
        eligibilityMode: data[providerKey].eligibilityMode,
        executionTime: data[providerKey].executionTime,
        commercialRangeResponses: data[providerKey].commercialRangeResponses,
        technicalInformation: data[providerKey].technicalInformation,
        fibreBuildingCodes: data[providerKey].fibreBuildingCodes,
        fibreBuildingCodesMismatch: data[providerKey].fibreBuildingCodesMismatch
      }
    });
  });

  public readonly COLUMN_DEFS = [
    {
      headerName: 'Opérateur',
      cellRenderer: ProviderResponseNameCellRendererComponent,
      cellRendererParams: (params: any) => this.technicalEligibilityApiService.getProviderDisplayedName(params.data.provider),
    },
    {
      headerName: 'Résultat',
      cellRenderer: IconCellRendererComponent,
      cellRendererParams: {
        iconParams: [
          {
            icon: 'check_circle',
            tooltip: 'Éligible',
            badge: (data: any) => data.fibreBuildingCodesMismatch ? '!' : null,
            condition: (data: any) => data.state === 'eligible',
            color: 'success-icon'
          },
          {
            icon: 'help',
            tooltip: 'Sur devis',
            condition: (data: any) => data.state === 'eligible_under_quotation',
          },
          {
            icon: 'warning',
            tooltip: 'Erreur',
            condition: (data: any) => data.state === 'error',
            color: 'warning-icon'
          },
          {
            icon: 'cancel',
            tooltip: 'Inéligible',
            condition: (data: any) => data.state === 'not_eligible',
            color: 'error-icon'
          }
        ]
      },
    },
    {
      headerName: 'Débit asc. max.',
      valueGetter: (params: ValueGetterParams) => params.data.maxUploadRate ? `${params.data.maxUploadRate} Mb/s` : '-'
    },
    {
      headerName: 'Débit desc. max.',
      valueGetter: (params: ValueGetterParams) => params.data.maxDownloadRate ? `${params.data.maxDownloadRate} Mb/s` : '-'
    },
    {
      headerName: 'Zone(s) tarifaire(s)',
      valueGetter: (params: ValueGetterParams) => params.data.pricingZones ? params.data.pricingZones.join(", ") : '-'
    },
    {
      headerName: "Méthode d'eligibilité",
      valueGetter: (params: ValueGetterParams) => {
        switch(params.data.eligibilityMode) {
          case 'imb':
            return 'Code immeuble';
          case 'pto':
            return 'PTO';
          case 'gps':
            return 'Coordonnées';
          case 'address':
            return 'Adresse';
          case 'nd':
            return 'ND';
          default:
            return '';
        }
      }
    },
    {
      cellRenderer: IconButtonCellRendererComponent,
      cellRendererParams: {
        icon: 'info',
        tooltip: 'Infos techniques complémentaires',
        onClick: (data: any) => this.displayTechnicalInformation(data),
        visible: (data: any) => Object.keys(data.technicalInformation ?? {}).length > 0
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      flex: 1,
      maxWidth: 75
    },
    {
      cellRenderer: IconButtonCellRendererComponent,
      cellRendererParams: {
        icon: 'apartment',
        tooltip: 'Codes immeuble',
        badge: (data: any) => data.fibreBuildingCodes ? data.fibreBuildingCodes.length : null,
        onClick: (data: any) => this.displayFibreBuildings(data),
        visible: (data: any) => data.fibreBuildingCodes && data.fibreBuildingCodes.length > 0
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      flex: 1,
      maxWidth: 75
    },
  ]

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: any,
    public readonly technicalEligibilityApiService: TechnicalEligibilityApiService,
    private readonly dialog: MatDialog
  ) {
    this.dataSignal.set(data.data);
  }

  displayTechnicalInformation(data: any) {
    this.dialog.open(TechnicalEligibilityTechnicalInfoModalComponent, {
      width: '50%',
      data: {
        provider: data.provider,
        technicalInformation: data.technicalInformation
      }
    });
  }

  displayFibreBuildings(data: any) {
    this.dialog.open(TechnicalEligibilityFibreBuildingsModalComponent, {
      width: '50%',
      data: {
        provider: data.provider,
        fibreBuildingCodes: data.fibreBuildingCodes
      }
    });
  }
}
